import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithAuth } from './api';
import {
  defaultHidden,
  defaultOrder,
  mergeSavedHidden,
  mergeSavedOrder,
  moveKey,
  orderColumns,
  stepKey,
} from './columnLayout';

// Per-user column layouts (issue #148, backend #133).
//
// The server is the source of truth so a layout follows the user between
// devices. localStorage holds a copy purely so the saved layout paints on the
// first render instead of flashing the defaults while the fetch is in flight;
// logout clears it along with everything else.

const CACHE_PREFIX = 'tableColumns.';
const SAVE_DELAY_MS = 600;

// GET /profile/tables returns every table at once, so one fetch serves whichever
// table the user opens next. Held at module scope for the life of the tab.
let allPreferences = null;
let inFlight = null;

const loadAllPreferences = () => {
  if (allPreferences) return Promise.resolve(allPreferences);
  if (!inFlight) {
    inFlight = fetchWithAuth('/profile/tables', { silent: true })
      .then(data => {
        allPreferences = data && typeof data === 'object' ? data : {};
        return allPreferences;
      })
      .finally(() => { inFlight = null; });
  }
  return inFlight;
};

const readCache = (tableKey) => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + tableKey);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeCache = (tableKey, payload) => {
  try {
    localStorage.setItem(CACHE_PREFIX + tableKey, JSON.stringify(payload));
  } catch (e) {
    // a full or disabled localStorage only costs the first-paint optimisation
  }
};

/**
 * @param tableKey  one of the keys the backend accepts (employees, clients,
 *                  billings, invoices, timesheets)
 * @param catalogue the table's columns in default order. Each entry needs a
 *                  `key` and `label`; `locked` pins it visible and in place,
 *                  `defaultHidden` starts it switched off.
 */
const useColumnPreferences = (tableKey, catalogue) => {
  const cached = useRef(readCache(tableKey)).current;
  const [order, setOrder] = useState(() => mergeSavedOrder(catalogue, cached));
  const [hidden, setHidden] = useState(() => mergeSavedHidden(catalogue, cached));
  const [saveError, setSaveError] = useState(null);
  const saveTimer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadAllPreferences()
      .then(all => {
        // a change made while the fetch was in flight wins over the server copy
        if (cancelled || dirty.current) return;
        const saved = all[tableKey] ?? null;
        setOrder(mergeSavedOrder(catalogue, saved));
        setHidden(mergeSavedHidden(catalogue, saved));
      })
      .catch(() => { /* keep the cached or default layout */ });
    return () => { cancelled = true; };
    // tables memoise their catalogue, so this settles after the first render;
    // the fetch itself is cached at module scope and the dirty guard keeps a
    // re-run from undoing a change the user just made
  }, [tableKey, catalogue]);

  const persist = useCallback((nextOrder, nextHidden) => {
    const payload = {
      visible: nextOrder.filter(key => !nextHidden.has(key)),
      hidden: nextOrder.filter(key => nextHidden.has(key)),
    };
    writeCache(tableKey, payload);
    if (allPreferences) allPreferences[tableKey] = payload;

    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetchWithAuth(`/profile/tables/${tableKey}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        silent: true,
      })
        .then(() => setSaveError(null))
        .catch(e => setSaveError(e.message || 'Column layout was not saved.'));
    }, SAVE_DELAY_MS);
  }, [tableKey]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const apply = useCallback((nextOrder, nextHidden) => {
    dirty.current = true;
    setOrder(nextOrder);
    setHidden(nextHidden);
    persist(nextOrder, nextHidden);
  }, [persist]);

  const toggle = useCallback((key) => {
    const col = catalogue.find(c => c.key === key);
    if (!col || col.locked) return;
    const nextHidden = new Set(hidden);
    if (nextHidden.has(key)) nextHidden.delete(key); else nextHidden.add(key);
    apply(order, nextHidden);
  }, [apply, catalogue, hidden, order]);

  const move = useCallback((key, targetKey) => {
    const col = catalogue.find(c => c.key === key);
    const target = catalogue.find(c => c.key === targetKey);
    if (!col || col.locked || !target || target.locked) return;
    apply(moveKey(order, key, targetKey), hidden);
  }, [apply, catalogue, hidden, order]);

  const step = useCallback((key, direction) => {
    const col = catalogue.find(c => c.key === key);
    if (!col || col.locked) return;
    apply(stepKey(catalogue, order, key, direction), hidden);
  }, [apply, catalogue, hidden, order]);

  const reset = useCallback(() => {
    apply(defaultOrder(catalogue), defaultHidden(catalogue));
  }, [apply, catalogue]);

  const ordered = useMemo(() => orderColumns(catalogue, order), [catalogue, order]);

  // what the table renders
  const columns = useMemo(
    () => ordered.filter(col => !hidden.has(col.key)),
    [ordered, hidden]
  );

  // what the picker lists: every column in the same order, with its state
  const pickerColumns = useMemo(
    () => ordered.map(col => ({ ...col, visible: !hidden.has(col.key) })),
    [ordered, hidden]
  );

  const isDefault = useMemo(() => {
    const defaults = defaultHidden(catalogue);
    const sameHidden = defaults.size === hidden.size && [...hidden].every(key => defaults.has(key));
    const sameOrder = ordered.every((col, index) => col.key === catalogue[index].key);
    return sameHidden && sameOrder;
  }, [catalogue, hidden, ordered]);

  return { columns, pickerColumns, toggle, move, step, reset, isDefault, saveError };
};

/**
 * Clears the filter belonging to a column the user just hid. A filter that
 * stays applied from a column nobody can see reads as missing rows with no
 * explanation, so hiding a column drops its filter.
 *
 * `emptyFilter` must be a stable reference (a module-level constant).
 */
export const useHiddenFilterReset = (columns, setFilter, emptyFilter) => {
  useEffect(() => {
    const shown = new Set(columns.filter(col => col.filter).map(col => col.filter.name));
    setFilter(prev => {
      let changed = false;
      const next = { ...prev };
      Object.keys(emptyFilter).forEach(key => {
        if (shown.has(key)) return;
        const isEmpty = Array.isArray(prev[key]) ? prev[key].length === 0 : !prev[key];
        if (isEmpty) return;
        next[key] = emptyFilter[key];
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [columns, setFilter, emptyFilter]);
};

export default useColumnPreferences;
